type CrudLike = {
  prefix?: string;
  meta?: {
    tableName: string;
    methods?: ("list" | "get" | "create" | "update" | "remove")[];
    allowedFilters?: string[];
    searchFields?: string[];
    sortable?: string[];
    cursor?: string[];
    disableILike?: boolean;
    prefix?: string;
    ownerRls?: { columnName: string; readPublic: boolean };
    allowAnonymous?: boolean;
  };
};

function collectCrudDefinitions(appModule: unknown): CrudLike[] {
  if (!appModule || typeof appModule !== "object") return [];

  const mod = appModule as { default?: unknown };
  const apiCandidate =
    mod.default !== undefined && mod.default !== null && typeof mod.default === "object"
      ? mod.default
      : appModule;
  const crud = (apiCandidate as { crud?: unknown }).crud;
  if (!crud || typeof crud !== "object") return [];

  return typeof (crud as CrudLike).prefix === "string"
    ? [crud as CrudLike]
    : Object.values(crud).filter((entry): entry is CrudLike => !!entry && typeof entry === "object");
}

/** Entries from API `crud` object with `createCrud`-style meta (always `crudImpl: "createCrud"`). */
export type CollectCrudCodegenMetaEntry = {
  prefix: string;
  tableName: string;
  methods: ("list" | "get" | "create" | "update" | "remove")[];
  allowedFilters: string[];
  searchFields: string[];
  sortable: string[];
  cursor: string[];
  disableILike?: boolean;
  allowAnonymous?: boolean;
  crudImpl: "createCrud";
};

export function collectCrudCodegenMeta(appModule: unknown): CollectCrudCodegenMetaEntry[] {
  const byPrefix = new Map<string, CollectCrudCodegenMetaEntry>();
  for (const def of collectCrudDefinitions(appModule)) {
    const meta = def.meta;
    const prefix =
      typeof def.prefix === "string"
        ? def.prefix
        : meta && typeof meta.prefix === "string"
          ? meta.prefix
          : undefined;
    if (!meta || typeof prefix !== "string") continue;
    byPrefix.set(prefix, {
      prefix,
      tableName: meta.tableName,
      methods: meta.methods ?? [],
      allowedFilters: meta.allowedFilters ?? [],
      searchFields: meta.searchFields ?? [],
      sortable: meta.sortable ?? [],
      cursor: meta.cursor ?? [],
      ...(meta.disableILike ? { disableILike: meta.disableILike } : {}),
      ...(meta.allowAnonymous ? { allowAnonymous: true as const } : {}),
      crudImpl: "createCrud",
    });
  }
  return [...byPrefix.values()];
}

export function collectOwnerRlsTables(appModule: unknown): Array<{
  tableName: string;
  columnName: string;
  readPublic: boolean;
}> {
  const dedup = new Map<string, { tableName: string; columnName: string; readPublic: boolean }>();
  for (const def of collectCrudDefinitions(appModule)) {
    const meta = def.meta;
    if (!meta?.ownerRls) continue;
    dedup.set(meta.tableName, {
      tableName: meta.tableName,
      columnName: meta.ownerRls.columnName,
      readPublic: meta.ownerRls.readPublic,
    });
  }

  return [...dedup.values()];
}
